/*
 * Copyright (c) CIRROM 2025.
 */

import { IntegratedSystem } from "../api/integratedsystems";
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSystemOrProject } from "../components/integrated-systems/hooks";
import {
  useGetWrittenChangesStats,
  useSystemDetails,
  useSystemStats,
} from "../hooks/components/dashboard/useDashboardData";
import { useDashboardStatsSplitByEntity } from "../hooks/components/dashboard/useDashboardStats";
import { Helmet } from "react-helmet";
import { LoadingDisplay } from "../components/dashboard/LoadingDisplay";
import { DashboardContactStats } from "../components/dashboard/DashboardContactStats";
import { Spacer } from "../components/utils/Spacer";
import { DashboardAccountStats } from "../components/dashboard/DashboardAccountStats";
import { Grid } from "@mui/material";
import { AutomaticAndManualLineChart } from "../components/dashboard/AutomaticAndManualLineChart";

interface ValidateDashboardProps {
  userSystems: IntegratedSystem[];
  setSelectedSystemId: (id: number) => void;
  dataProjectId?: number;
}

export const ValidateDashboard = React.memo(
  (props: ValidateDashboardProps) => {
    const navigate = useNavigate();
    const { integratedSystemId } = useParams();

    useEffect(() => {
      if (integratedSystemId) {
        props.setSelectedSystemId(Number.parseInt(integratedSystemId));
      }
    }, [integratedSystemId]);

    const systemOrProject = useSystemOrProject(
      props.userSystems,
      integratedSystemId
    );

    const systemStats = useSystemStats(systemOrProject, props.dataProjectId);

    const statsByEntity = useDashboardStatsSplitByEntity(
      systemStats.systemStats,
      integratedSystemId,
      props.dataProjectId != null
    );

    const systemDetails = useSystemDetails(systemOrProject);

    const writtenChangesStats = useGetWrittenChangesStats(
      systemOrProject,
      props.dataProjectId
    );

    useEffect(() => {
      if (
        !integratedSystemId &&
        props.userSystems &&
        props.userSystems.length > 0
      ) {
        navigate(`/dashboard/${props.userSystems[0].id}`);
      }
    }, [integratedSystemId, props.userSystems, navigate]);

    if (systemDetails.isLoading) {
      return (
        <React.Fragment>
          <Helmet title="Cirrom" />
          <LoadingDisplay />
        </React.Fragment>
      );
    }

    if (!systemOrProject) {
      return (
        <React.Fragment>
          <Helmet title="Cirrom" />
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <Helmet title="Cirrom" />

        <DashboardContactStats
          stats={statsByEntity.contact}
          isLoading={systemStats.isLoading}
        />
        <Spacer />
        <DashboardAccountStats
          stats={statsByEntity.account}
          isLoading={systemStats.isLoading}
        />
        <Spacer />
        <Grid container spacing={6}>
          <Grid
            size={{
              xs: 12,
              sm: 12,
              md: 7,
            }}
          >
            <AutomaticAndManualLineChart
              writtenChanges={writtenChangesStats.writtenChangesStats}
              systemType={systemOrProject?.system_type}
            />
          </Grid>
        </Grid>
      </React.Fragment>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.userSystems === nextProps.userSystems &&
      prevProps.dataProjectId === nextProps.dataProjectId &&
      prevProps.setSelectedSystemId === nextProps.setSelectedSystemId
    );
  }
);
